import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { ReactTable } from "components";
import { useShowPriceInVat } from "hooks";
import { DMS_CAPABILITIES, INTERVENTION_ELEMENT_STATUSES } from "models";
import { InterventionElementIcon } from "modules/AppointmentDetails/components/Interventions/components/Subitems/components/InterventionElementIcon";
import "modules/AppointmentDetails/components/Interventions/components/Subitems/components/SubitemsTable/SubitemsTable.scss";
import { enumToDropdownOptions, getPriceInVAT } from "utils/common";
import { formatPrice } from "utils/formatPrice";

import type { DealerLocation } from "models";
import type { InterventionElement } from "models/InterventionElement";

type SubitemsTableProps = {
  elements?: InterventionElement[];
  location?: DealerLocation | null;
  onDeleteSubitem: (element: InterventionElement) => void;
  onEdit: (element: InterventionElement) => void;
};

const columnHelper = createColumnHelper<InterventionElement>();

export const SubitemsTable = ({ elements, location, onDeleteSubitem, onEdit }: SubitemsTableProps) => {
  const { t } = useTranslation();

  const showPriceInVat = useShowPriceInVat();

  const interventionElementStatuses = enumToDropdownOptions({ enumObj: INTERVENTION_ELEMENT_STATUSES, t });
  const hasManualSubitems = elements?.some(subitem => subitem.is_local);

  let columns = [
    columnHelper.accessor(row => row, {
      id: "element_type",
      header: t("v8_type", "Type"),
      size: 20,
      cell: ({ row: { original } }) => <InterventionElementIcon elementType={original.element_type} parentDmsNr={original.parent_dms_nr} />
    }),
    columnHelper.accessor(row => row, {
      id: "id",
      cell: ({ row: { original } }) => `${original.brand_code} ${original.reference_code}`.trim(),
      size: 40,
      header: () => "#"
    }),
    columnHelper.accessor(row => row, {
      id: "description",
      header: () => t("v8_description", "Description"),
      size: 520,
      cell: ({ row: { original } }) => original.description
    }),
    columnHelper.accessor(row => row, {
      id: "unit",
      header: () => <span className="ellipsis">{t("v8_unit", "Unit")}</span>,
      size: 60,
      cell: ({
        row: {
          original: { price, vat }
        }
      }) => {
        const formattedPriceValue = Number(price || 0);
        const formattedPrice = showPriceInVat ? formatPrice(getPriceInVAT(formattedPriceValue, location, vat)) : formatPrice(formattedPriceValue);
        return "€ " + formattedPrice;
      }
    }),
    columnHelper.accessor(row => row, {
      id: "quantity",
      header: () => <span className="ellipsis">{t("v8_quantity", "Quantity")}</span>,
      size: 40,
      cell: ({ row: { original } }) => original.quantity
    }),
    columnHelper.accessor(row => row, {
      id: "price",
      header: () => <span className="ellipsis">{t("v8_price", "Price")}</span>,
      size: 60,
      cell: ({
        row: {
          original: { price, quantity, vat }
        }
      }) => {
        const total = Number(price || 0) * Number(quantity || 1);
        return "€ " + (showPriceInVat ? formatPrice(getPriceInVAT(total, location, vat)) : formatPrice(total));
      }
    })
  ];

  const statusColumn = columnHelper.accessor(row => row, {
    id: "status",
    header: () => <span className="ellipsis">{t("v8_status", "Status")}</span>,
    size: 60,
    cell: ({ row: { original } }) => (original.status ? interventionElementStatuses.find(({ key }) => key === original.status)?.text || "" : "")
  });

  const actionsColumn = columnHelper.accessor(row => row, {
    id: "action",
    header: "",
    size: 60,
    cell: ({ row: { original } }) => (
      <div className="SubitemsTable__actions">
        {(original.is_local || location?.dms_capability_ids?.includes(DMS_CAPABILITIES.UpdateInterventionElement)) && (
          <div className="subitem-action" onClick={() => onEdit(original)}>
            <Icon className="pen grey" />
          </div>
        )}
        {(original.is_local || location?.dms_capability_ids?.includes(DMS_CAPABILITIES.DeleteInterventionElement)) && (
          <div className="subitem-action" onClick={() => onDeleteSubitem(original)}>
            <Icon className="trash grey" />
          </div>
        )}
      </div>
    )
  });

  if (location?.dms_capability_ids?.includes(DMS_CAPABILITIES.InterventionElementPartStatus)) columns = [...columns, statusColumn];
  if (
    hasManualSubitems ||
    location?.dms_capability_ids?.includes(DMS_CAPABILITIES.UpdateInterventionElement) ||
    location?.dms_capability_ids?.includes(DMS_CAPABILITIES.DeleteInterventionElement)
  )
    columns = [...columns, actionsColumn];

  return (
    <div className="SubitemsTable">
      <ReactTable columns={columns} data={elements} />
    </div>
  );
};
